/**
 * CDX Infinite Scroll for Impact Theme
 * ------------------------------------
 * Replaces pagination with automatic infinite scroll.
 * Works with the existing facet:update system.
 * Falls back to visible pagination when JS is disabled (SEO-safe).
 */

(function () {
  const SELECTOR = {
    section: '.shopify-section--main-collection',
    productList: '.collection__results product-list',
    pagination: '.collection__pagination',
    nextLink: '.collection__pagination a[rel="next"]',
    sentinel: '#cdx-infinite-sentinel',
    loader: '#cdx-infinite-loader',
  };

  let observer = null;
  let isLoading = false;

  function init() {
    const section = document.querySelector(SELECTOR.section);
    if (!section) return;

    const pagination = section.querySelector(SELECTOR.pagination);
    const productList = section.querySelector(SELECTOR.productList);
    if (!productList) return;

    // Hide pagination (keep in DOM for SEO)
    if (pagination) {
      pagination.style.display = 'none';
    }

    // Create or find sentinel & loader
    let sentinel = section.querySelector(SELECTOR.sentinel);
    let loader = section.querySelector(SELECTOR.loader);

    if (!sentinel) {
      const wrapper = document.createElement('div');
      wrapper.id = 'cdx-infinite-wrap';
      wrapper.innerHTML = `
        <div id="cdx-infinite-loader" style="display:none;text-align:center;padding:2rem 0;">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style="animation:cdx-spin 0.8s linear infinite;">
            <circle cx="12" cy="12" r="10" stroke="#ecf3f3" stroke-width="3"/>
            <path d="M12 2a10 10 0 019.8 8" stroke="#3a8d95" stroke-width="3" stroke-linecap="round"/>
          </svg>
        </div>
        <div id="cdx-infinite-sentinel" style="height:1px;"></div>
      `;

      // Insert after .collection__results
      const results = section.querySelector('.collection__results');
      if (results) {
        results.parentNode.insertBefore(wrapper, results.nextSibling);
      }

      sentinel = wrapper.querySelector(SELECTOR.sentinel);
      loader = wrapper.querySelector(SELECTOR.loader);
    }

    // No next page? Nothing to observe
    const nextLink = section.querySelector(SELECTOR.nextLink);
    if (!nextLink) return;

    // Set up IntersectionObserver
    if (observer) observer.disconnect();

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoading) {
            loadNextPage(section);
          }
        });
      },
      { rootMargin: '0px 0px 600px 0px' }
    );

    observer.observe(sentinel);
  }

  async function loadNextPage(section) {
    const nextLink = section.querySelector(SELECTOR.nextLink);
    if (!nextLink || isLoading) return;

    isLoading = true;
    const loader = document.querySelector(SELECTOR.loader);
    if (loader) loader.style.display = 'block';

    try {
      const url = new URL(nextLink.href);
      // Add section_id for section rendering
      const sectionId = section.id.replace('shopify-section-', '');
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString());
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Extract new product cards
      const newProductList = doc.querySelector(SELECTOR.productList);
      if (newProductList) {
        const currentProductList = section.querySelector(SELECTOR.productList);
        const newItems = newProductList.children;

        // Append each new product with reveal animation
        const fragment = document.createDocumentFragment();
        Array.from(newItems).forEach((item) => {
          item.style.opacity = '0';
          item.style.transform = 'translateY(20px)';
          item.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          fragment.appendChild(item);
        });

        currentProductList.appendChild(fragment);

        // Trigger reveal animation
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            currentProductList.querySelectorAll('[style*="opacity: 0"]').forEach((el, i) => {
              setTimeout(() => {
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 60);
            });
          });
        });

      }

      // Update pagination (hidden) with new next/prev links
      const newPagination = doc.querySelector(SELECTOR.pagination);
      const currentPagination = section.querySelector(SELECTOR.pagination);
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
        currentPagination.style.display = 'none';
      } else if (currentPagination) {
        // No more pages
        currentPagination.innerHTML = '';
      }

    } catch (e) {
      console.error('[CDX Infinite Scroll]', e);
    } finally {
      isLoading = false;
      if (loader) loader.style.display = 'none';
    }
  }

  // Add spinner animation
  if (!document.querySelector('#cdx-infinite-style')) {
    const style = document.createElement('style');
    style.id = 'cdx-infinite-style';
    style.textContent = '@keyframes cdx-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after facet updates (filters/sort change replaces section content)
  // Use MutationObserver to detect when section content is replaced
  const sectionEl = document.querySelector(SELECTOR.section);
  if (sectionEl) {
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'childList' && m.addedNodes.length > 0) {
          // Section content was replaced, re-init
          if (observer) observer.disconnect();
          isLoading = false;
          requestAnimationFrame(() => init());
          break;
        }
      }
    });
    mo.observe(sectionEl, { childList: true });
  }
})();
